import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

export default function AdminAnnouncements() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ title: "", body: "", audience: "all" });

  const { data: history = [] } = useQuery({
    queryKey: ["admin-announcements"],
    queryFn: async () => (await api.get("/admin/announcements")).data,
  });

  const send = useMutation({
    mutationFn: async () => (await api.post("/admin/announcements", form)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["admin-announcements"] });
      toast.success(`Announcement sent to ${d.recipient_count} user(s).`);
      setForm({ title: "", body: "", audience: "all" });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/announcements/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-announcements"] });
      toast.success("Announcement deleted.");
    },
  });

  const submit = (e) => { e.preventDefault(); send.mutate(); };

  return (
    <div data-testid="admin-announcements-page" className="grid lg:grid-cols-2 gap-6">
      <Card className="p-6 bg-card border-border h-fit">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="h-5 w-5 text-primary" weight="bold" />
          <h3 className="text-lg font-black">Compose Announcement</h3>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input required data-testid="announcement-title" className="mt-1.5 bg-secondary/50"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="New feature released!" />
          </div>
          <div>
            <Label>Body (markdown)</Label>
            <Textarea required data-testid="announcement-body" className="mt-1.5 bg-secondary/50 min-h-[140px]"
              value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Tell your users what's new…" />
          </div>
          <div>
            <Label>Audience</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="announcement-audience" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                <SelectItem value="pro">Pro users only</SelectItem>
                <SelectItem value="free">Free users only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={send.isPending} data-testid="announcement-send" className="rounded-md font-semibold">
            <PaperPlaneTilt className="h-4 w-4 mr-1.5" weight="bold" /> {send.isPending ? "Sending…" : "Send Announcement"}
          </Button>
        </form>
      </Card>

      <div>
        <h3 className="text-base font-bold mb-3">History</h3>
        <div className="space-y-3" data-testid="announcement-history">
          {history.length === 0 && <p className="text-sm text-muted-foreground">No announcements yet.</p>}
          {history.map((a) => (
            <Card key={a.id} className="p-4 bg-card border-border" data-testid={`announcement-${a.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-semibold">{a.title}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{a.audience}</span>
                  <button data-testid={`delete-announcement-${a.id}`} onClick={() => remove.mutate(a.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                    <Trash className="h-4 w-4" weight="bold" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 whitespace-pre-wrap">{a.body}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {a.created_at ? format(new Date(a.created_at), "MMM d, yyyy · h:mm a") : ""} · {a.recipient_count} recipient(s)
              </p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
